// src\app\[lang]\components\ShareButton.tsx
"use client";
import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button, Snackbar, Fade
} from '@mui/material';
import {
  ShareIcon,
  PrinterIcon as PrintIcon,
  EnvelopeIcon as EmailIcon,
  DocumentDuplicateIcon as ContentCopyIcon
} from '@heroicons/react/24/solid';

// Social media icon components using simple SVG paths
const FacebookIcon = ({ fontSize }: { fontSize?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: fontSize === 'small' ? '20px' : '24px', height: fontSize === 'small' ? '20px' : '24px' }}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TwitterIcon = ({ fontSize }: { fontSize?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: fontSize === 'small' ? '20px' : '24px', height: fontSize === 'small' ? '20px' : '24px' }}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const ShareButton: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showCopyFeedback, setShowCopyFeedback] = useState(false);

  const handleShareClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setAnchorEl(null);
  };

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(window.location.href);
      } else {
        // Fallback for mobile browsers
        const textarea = document.createElement('textarea');
        textarea.value = window.location.href;
        textarea.style.position = 'fixed';  // Avoid scrolling to bottom
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
          document.execCommand('copy');
        } catch (err) {
          console.error('Fallback: Oops, unable to copy', err);
        }
        document.body.removeChild(textarea);
      }
      setShowCopyFeedback(true);
      handleShareClose();
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCloseSnackbar = () => {
    setShowCopyFeedback(false);
  };

  const handlePrint = () => {
    handleShareClose();
    window.print();
  };

  const handleShareToTwitter = () => {
    const text = encodeURIComponent(document.title);
    const url = encodeURIComponent(window.location.href);
    const twitterShareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    window.open(twitterShareUrl, '_blank', 'noopener,noreferrer');
    handleShareClose();
  };

  const handleShareToFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    window.open(facebookShareUrl, '_blank', 'noopener,noreferrer');
    handleShareClose();
  };

  const handleShareViaEmail = () => {
    const subject = encodeURIComponent('Check out this page');
    const body = encodeURIComponent(
      `I thought you might find this interesting:\n\n${window.location.href}`
    );
    const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
    handleShareClose();
  };

  return (
    <div className="flex justify-end print:hidden">
      <Button
        onClick={handleShareClick}
        startIcon={<ShareIcon className="w-5 h-5" />}
        sx={{
          display: 'flex',
          alignitems: 'center',
          justifyContent: 'center',
          border: '2px solid #B00909',
          color: '#B00909',
          backgroundColor: '#EAEAE2',
          borderRadius: '9999px', // This makes it very rounded
          '&:hover': {
            backgroundColor: '#B00909',
            color: '#EAEAE2',
            border: '2px solid #B00909',
          },
          textTransform: 'none',
          padding: '4px 12px',
          // fontSize: '1rem',
          lineHeight:'1',
          fontWeight: 'bold',
          height: '32px'
        }}
      >
        Share
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleShareClose}
        disableScrollLock
      >
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon>
            <ContentCopyIcon className="w-5 h-5" />
          </ListItemIcon>
          <ListItemText primary="Copy Link" />
        </MenuItem>

        <MenuItem onClick={handlePrint}>
          <ListItemIcon>
            <PrintIcon className="w-5 h-5" />
          </ListItemIcon>
          <ListItemText primary="Print" />
        </MenuItem>

        <MenuItem onClick={handleShareToTwitter}>
          <ListItemIcon>
            <TwitterIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Twitter" />
        </MenuItem>

        <MenuItem onClick={handleShareToFacebook}>
          <ListItemIcon>
            <FacebookIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Facebook" />
        </MenuItem>

        <MenuItem onClick={handleShareViaEmail}>
          <ListItemIcon>
            <EmailIcon className="w-5 h-5" />
          </ListItemIcon>
          <ListItemText primary="Email" />
        </MenuItem>
      </Menu>

      <Snackbar
        open={showCopyFeedback}
        autoHideDuration={1200}
        onClose={handleCloseSnackbar}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Fade}
        TransitionProps={{timeout: 600}}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#B00909',
            color: '#EAEAE2',
            textAlign: 'center',
            '& .MuiSnackbarContent-message': {
              fontSize: '1rem',  // Makes text slightly larger
              width: '100%',       // Ensures full width for center alignment
              textAlign: 'center'  // Centers the text
            }
          }
        }}
      />

    </div>
  );
};

export default ShareButton;
